import { BlocksApiNewsAttachment } from "@/src/models/news.model";
import gqlClient from "@/src/services/api/graphql.client";
import {
  CREATE_POST_MUTATION,
  DELETE_POST_MUTATION,
  GET_POSTS_QUERY,
  UPDATE_POST_MUTATION,
} from "@/src/services/api/posts/posts.query";
import { hot } from "@/src/utils/feed-score";
import {
  buildNewsPostAttachmentUrls,
  buildNewsPostAudience,
  buildNewsPostContent,
} from "@/src/utils/news-post.utils";

/**
 * Mirroring a published news article into the feed.
 *
 * A published article is copied into a normal `post` row carrying the article's
 * id in `newsId`. The duplication is intentional: the feed keeps one entity, one
 * paged query, one sort and one visibility rule, and the article picks up
 * reactions, comments, saves and sharing for free. `newsId` is both the flag the
 * card renders from and the link back to the article.
 *
 * Everything here is best-effort. The article itself is already written by the
 * time these run, so a failure to mirror must never be reported as a failed
 * publish — callers log and carry on (the same contract `sendNotification` has).
 */

export type NewsFeedPostAuthor = Readonly<{
  firstName: string;
  lastName: string;
  email: string;
  profileImageUrl?: string;
  userId?: string;
}>;

export type NewsFeedPostInput = Readonly<{
  newsId: string;
  title: string;
  content: string;
  coverImageUrl?: string | null;
  attachments?: readonly Pick<BlocksApiNewsAttachment, "url">[];
  /** News visibility, i.e. "Public" | "Group". */
  visibility?: string | null;
  groupIds?: readonly string[];
  author: NewsFeedPostAuthor;
}>;

const request = async (query: string, variables?: object) => {
  const client = await gqlClient();
  return client.request(query, variables);
};

/**
 * The id of the post already mirroring this article, or null.
 *
 * Returns null rather than throwing when the lookup fails so a gateway that does
 * not know the `newsId` field yet degrades to "no mirror exists" instead of
 * taking the publish down with it.
 */
const findNewsPostId = async (newsId: string): Promise<string | null> => {
  try {
    const result: any = await request(GET_POSTS_QUERY, {
      where: { newsId: { eq: newsId } },
      order: [{ field: "CreatedDate", direction: "DESC" }],
      pageNo: 1,
      pageSize: 1,
    });
    return result?.getposts?.items?.[0]?.ItemId ?? null;
  } catch (error) {
    console.warn("news mirror: lookup failed", error);
    return null;
  }
};

/**
 * Create the feed post for an article, or bring the existing one back in step
 * after an edit.
 *
 * Update deliberately rewrites only what the author can change — body, audience,
 * attachments, headline. Reactions, comments, saves and the hotness score belong
 * to the post and must survive an edit of the article.
 *
 * Returns the mirrored post's id, or null when nothing could be written.
 */
export const syncNewsFeedPost = async (
  input: NewsFeedPostInput,
): Promise<string | null> => {
  const { newsId, title, content, author } = input;
  if (!newsId) return null;

  const postContent = buildNewsPostContent(title, content);
  const attachmentUrls = buildNewsPostAttachmentUrls(
    input.coverImageUrl,
    input.attachments,
  );
  const { visibility, visibleToGroupIds } = buildNewsPostAudience(
    input.visibility,
    input.groupIds,
  );

  const existingPostId = await findNewsPostId(newsId);

  if (existingPostId) {
    await request(UPDATE_POST_MUTATION, {
      itemId: existingPostId,
      content: postContent,
      hashtags: [],
      mentionedUsers: [],
      visibility,
      attachmentUrls,
      visibleToGroupIds,
      visibleToUserIds: [],
      newsTitle: title,
    });
    return existingPostId;
  }

  const result: any = await request(CREATE_POST_MUTATION, {
    content: postContent,
    hashtags: [],
    mentionedUsers: [],
    visibility,
    attachmentUrls,
    // A news mirror is never scoped to a single group's own feed: a Group
    // article can address several, which is what `visibleToGroupIds` carries.
    groupId: null,
    visibleToGroupIds,
    visibleToUserIds: [],
    score: hot(0, 0, new Date()),
    lifeEventType: null,
    lifeEventDate: null,
    newsId,
    newsTitle: title,
    firstName: author.firstName,
    lastName: author.lastName,
    email: author.email,
    profileImageUrl: author.profileImageUrl ?? "",
    userId: author.userId ?? "",
  });

  return result?.insertpost?.itemId ?? null;
};

/**
 * Remove the feed post for an article.
 *
 * Called when the article is deleted, and when a published article is pushed
 * back to draft — a draft has no place in the feed. A no-op when no mirror
 * exists, which is the normal case for an article published before this
 * existed.
 */
export const deleteNewsFeedPost = async (newsId: string): Promise<void> => {
  if (!newsId) return;

  const existingPostId = await findNewsPostId(newsId);
  if (!existingPostId) return;

  await request(DELETE_POST_MUTATION, { itemId: existingPostId });
};
